import { LevelSim, SimConstants, petArrival, petForecast } from "@highmarch/domain";
import { FLYER_HEIGHT } from "./common";
import { PET_STRIKE_TUNING } from "./petStrike";
import type { FightCue, FoeAt } from "./petFight";

/**
 * Before any raider is in reach the sim's own dry run (domain petArrival) names the first to walk in and its tick, at most
 * `maxTicks` ahead and re-read every `every` ticks (it carries every raider along the road: not for each tick on a
 * phone). `margin`: a raider it fights is let go this far (cells) out of reach.
 */
const WATCH = { maxTicks: 80, every: 3, margin: 0.3 } as const;
/** Obstacles the fight steers round: at most this many (troops and other raiders), refreshed each tick. */
const MAX_OBS = 48;

/**
 * What the pet's fight (petFight.ts) reads of the sim, read-only, once a tick: the sim's own forecast of the next attack
 * (domain petForecast: the raider the attack function picks and the ticks until it may strike) — and, while no raider
 * is in reach, the sim's dry run of who walks in first and when (domain petArrival); the troops and other raiders to
 * steer round; where a raider is drawn. No rule of the sim's is re-implemented here.
 */
export class PetFoes {
  /** The attack's cadence (seconds), 0 with no attack. */
  readonly interval: number;
  /** Steering obstacles (x, z pairs) and how many. */
  readonly obstacles = new Float64Array(MAX_OBS * 2);
  count = 0;
  private readonly cue = { target: -1, inTicks: 0, sure: false, stalk: -1 };
  /** The attack's reach round the flag's cell (cells). */
  reach = 0;
  /** The last dry run (re-read every WATCH.every ticks) and the tick it was read. */
  private arrival: { target: number; tick: number } = { target: -1, tick: -1 };
  private readAt = -Infinity;
  private readonly foe = { x: 0, z: 0, lift: 0 };

  constructor(private readonly sim: LevelSim, private readonly drawnAt: (id: number) => { readonly x: number; readonly z: number } | null) {
    const f = petForecast(sim);
    this.interval = f ? f.intervalTicks / SimConstants.TicksPerSecond : 0;
    this.reach = f ? f.reach.toNumber() : 0;
  }

  /** Whether raider `id` is still about the attack's reach of the flag (a sim read, no rule: the reach is the forecast's). */
  readonly near = (id: number): boolean => {
    const e = this.sim.findEnemy(id), r = this.sim.troops.rally;
    return !!e && Math.hypot(e.x.toNumber() - r.x, e.y.toNumber() - r.y) <= this.reach + WATCH.margin;
  };

  /**
   * After each tick: the sim's next attack (null in a run without one) — the raider in reach and its tick (domain
   * petForecast), or with none in reach the first to walk in and its tick (domain petArrival) — and the obstacles round it.
   */
  read(): FightCue | null {
    const sim = this.sim, f = petForecast(sim);
    this.refreshObstacles();
    if (!f) return null;
    const c = this.cue;
    c.sure = f.target >= 0; c.target = f.target; c.inTicks = f.inTicks; c.stalk = -1;
    if (f.target >= 0) { this.arrival.target = -1; return c; }
    if (sim.tick - this.readAt >= WATCH.every || (this.arrival.target >= 0 && !sim.findEnemy(this.arrival.target))) {
      const a = petArrival(sim, WATCH.maxTicks);
      this.readAt = sim.tick;
      this.arrival.target = a?.target ?? -1; this.arrival.tick = a ? sim.tick + a.inTicks : -1;
    }
    if (this.arrival.target < 0 || this.arrival.tick <= sim.tick) return c;
    c.stalk = this.arrival.target; c.target = this.arrival.target; c.inTicks = this.arrival.tick - sim.tick;
    return c;
  }

  /** Where raider `id` is drawn, and how high the owl strikes it from (FoeAt; one object, reused), or null. */
  readonly at = (id: number): FoeAt | null => {
    const p = this.drawnAt(id);
    if (!p) return null;
    const air = this.sim.findEnemy(id)?.isAir ?? false;
    this.foe.x = p.x; this.foe.z = p.z; this.foe.lift = air ? FLYER_HEIGHT - PET_STRIKE_TUNING.underFlyer : 0;
    return this.foe;
  };

  /** The troops and the ground raiders (its own raider too: it goes round it to its side), x, z pairs (sim positions). */
  private refreshObstacles(): void {
    const o = this.obstacles;
    let n = 0;
    for (const t of this.sim.troops.slots) {
      if (n >= MAX_OBS) break;
      if (!t.alive) continue;
      o[2 * n] = t.x.toNumber(); o[2 * n + 1] = t.y.toNumber(); n++;
    }
    for (const e of this.sim.enemySlots) {
      if (n >= MAX_OBS) break;
      if (!e.alive || e.isAir) continue;
      o[2 * n] = e.x.toNumber(); o[2 * n + 1] = e.y.toNumber(); n++;
    }
    this.count = n;
  }
}
